import Component from '@glimmer/component';
import { formatInTimeZone } from 'date-fns-tz';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and } from 'ember-truth-helpers';
import type VisitComposite from 'garaje/models/visit-composite';
import type VisitorProfile from 'garaje/models/visitor-profile';
import { formatDateTimeInSystemTimezone } from 'garaje/utils/date-fns-tz-utilities';

type RelevantVisitSummary = {
  message: string;
  class: string;
};

interface VisitCellSignature {
  Args: {
    row: VisitorProfile;
  };
}

export default class VisitCell extends Component<VisitCellSignature> {
  get relevantSummary(): RelevantVisitSummary | null {
    if (!this.visit) return null;

    switch (this.visit.visitType) {
      case 'previous':
        return {
          message: 'Last visit',
          class: 'text-carbon-70',
        };
      case 'current':
        return {
          message: 'Currently visiting',
          class: 'font-bold text-gem-60',
        };
      case 'upcoming':
        return {
          message: 'Next visit',
          class: 'font-bold text-carbon-80',
        };
      case 'no_show':
        return {
          message: 'Last visit (no show)',
          class: 'text-carbon-50',
        };
      default:
        return null;
    }
  }

  get visit(): VisitComposite | null {
    return this.args.row.relevantVisitSync;
  }

  get formattedVisitTimeInLocationTimezone(): string {
    const visitTime = this.visit?.visitTime;
    if (!visitTime) return '';
    return formatInTimeZone(visitTime, this.visit.locationTimezone, 'MMM d, yyyy');
  }

  get dateTimeInSystemTimezone(): string {
    const visitTime = this.visit?.visitTime;
    if (!visitTime) return '';
    return formatDateTimeInSystemTimezone(visitTime);
  }

  <template>
    <div class="flex" data-test-visit-cell>
      {{#if (and this.visit this.relevantSummary)}}
        <div class="flex flex-col" data-test-visit-cell-content>
          <EmberTooltip @side="top-start" @text={{this.dateTimeInSystemTimezone}} @tooltipClass="visit-cell-tooltip" />
          <span
            class={{this.relevantSummary.class}}
            data-test-visit-cell-message
          >{{this.relevantSummary.message}}</span>
          <span class="text-carbon-50" data-test-visit-cell-date>{{this.formattedVisitTimeInLocationTimezone}}</span>
        </div>
      {{else}}
        <span class="text-carbon-50">
          No visit information
        </span>
      {{/if}}
    </div>
  </template>
}
